import { ClerkProvider } from "@clerk/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { initI18n, DEFAULT_LANGUAGE } from "@noa/i18n";
import { AppConfigsQueryKey, getApiErrorI18nKey, initApiClient } from "@noa/api-client";
import LanguageDetector from "i18next-browser-languagedetector";
import i18next from "i18next";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import { toast } from "sonner";
import { App } from "./app.tsx";
import appConfig from "./configs/app.ts";
import { loadAppConfigs } from "./configs/app-configs.ts";
import { localLanguageStorage } from "./utils/language-storage.ts";
import { useApplyThemeMode } from "./hooks/use-apply-theme-mode.ts";
import { getAuthToken } from "@noa/client-store";
import "./styles/index.css";

if (!localLanguageStorage.load()) localLanguageStorage.store(DEFAULT_LANGUAGE);

initApiClient({
  baseUrl: appConfig.apiBaseUrl,
  getToken: getAuthToken,
  getLanguage: () => i18next.language,
  onError: (error) => {
    toast.error(i18next.t(getApiErrorI18nKey(error)), {
      description: error.message,
    });
  },
});

const queryClient = new QueryClient();

export const AppProviders = ({ children }: { children: React.ReactNode }) => {
  // Applies data-theme + color-scheme to <html>
  useApplyThemeMode();

  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ClerkProvider publishableKey={appConfig.clerkPublishableKey} appearance={{ baseTheme: "simple" }}>
            {children}
          </ClerkProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>
  );
};

const root = document.getElementById("root");
if (!root) {
  throw new Error("[main] root element not found");
}

/**
 * The deployment's configuration file gates the first render: i18n has to know the product name
 * before anything paints, and seeding the query cache spares `useAppConfigs` a second fetch.
 *
 * The render sits in `finally`: whatever goes wrong while applying the configuration, the user gets
 * the app on its bundled strings rather than an empty page.
 */
const bootstrap = async () => {
  try {
    const appConfigs = await loadAppConfigs();
    if (appConfigs) queryClient.setQueryData(AppConfigsQueryKey.all, appConfigs);

    initI18n({ plugins: [LanguageDetector], branding: appConfigs?.branding });
    // index.html ships the default name; the tab follows the deployment's once it is known.
    document.title = i18next.t("about:headertitle");
  } finally {
    createRoot(root).render(
      <AppProviders>
        <App />
      </AppProviders>
    );
  }
};

void bootstrap();
